/**
 * ConfirmModal — drop-in replacement for `window.confirm` /
 * `window.prompt`, styled with the app's surface tokens so it doesn't
 * jar against the rest of the proposal admin UI.
 *
 * Replaces the native browser confirm in flows where:
 *   - The confirmation copy is more than a single line (e.g. send /
 *     withdraw / delete proposal flows want multi-line context)
 *   - Dark-mode readability matters (native confirm uses OS chrome,
 *     so it always reads OK, but the JUMP to OS chrome breaks flow)
 *   - We want an optional REASON field (replaces the
 *     `prompt() + confirm()` pair the withdraw flow used to do)
 *
 * API mirrors the use cases we have today:
 *   - `open` controls visibility
 *   - `title` + `description` are the primary copy
 *   - `confirmLabel` / `cancelLabel` customise the buttons
 *   - `variant: 'danger'` paints the confirm button red for
 *     destructive actions (delete / withdraw / regenerate-token);
 *     `variant: 'neutral'` paints it in the text colour (screens that keep
 *     their one brand accent for something else — the V2 builder's Send)
 *   - `requireReason` adds a textarea — `onConfirm(reason)` receives
 *     the trimmed value (or undefined when `requireReason` is false)
 *   - `loading` disables the buttons + shows a spinner on confirm
 *
 * The modal is uncontrolled w.r.t. the reason textarea: each open
 * starts with empty input, every close clears state. Callers don't
 * have to manage that themselves.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

interface Props {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger' | 'neutral';
  /** When true, surfaces a textarea. The value (trimmed, empty
   *  allowed) is passed to onConfirm. */
  requireReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  /** When true, REQUIRES the textarea to be non-empty before confirm
   *  enables. Used for "Decline reason" style flows. */
  reasonRequired?: boolean;
  /** Spinner + disabled buttons while the parent's async work runs. */
  loading?: boolean;
  /** Extra fields under the description (e.g. an editable recipient). Additive — absent renders as before. */
  children?: ReactNode;
  /** Hold the confirm button disabled (the caller's own validation). */
  confirmDisabled?: boolean;
  onConfirm: (reason?: string) => void;
  onClose: () => void;
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  requireReason = false,
  reasonLabel = 'Reason',
  reasonPlaceholder,
  reasonRequired = false,
  loading = false,
  children,
  confirmDisabled: callerDisabled = false,
  onConfirm,
  onClose,
}: Props) {
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset reason every time the modal re-opens.
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  // Esc to cancel. Focus the textarea when present so the office can
  // start typing immediately.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', handler);
    if (requireReason) {
      // Defer to next tick — Radix-style autoFocus inside an animated
      // modal is unreliable; a microtask after mount works in every
      // browser without needing focus traps.
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
    return () => window.removeEventListener('keydown', handler);
  }, [open, loading, onClose, requireReason]);

  if (!open) return null;

  const trimmed = reason.trim();
  const confirmDisabled = loading || callerDisabled || (reasonRequired && trimmed === '');
  const confirmClasses =
    variant === 'danger'
      ? 'bg-rose-600 hover:bg-rose-700 text-white'
      : variant === 'neutral'
        ? 'bg-[var(--text-primary)] text-[var(--surface-card)] hover:opacity-90'
        : 'bg-brand-secondary hover:opacity-90 text-white';

  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm(requireReason ? trimmed : undefined);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        // Click on backdrop (not inside the dialog) closes. Disabled
        // while loading so an accidental click can't escape a
        // half-committed action.
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-theme bg-surface-card shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-3">
          {variant === 'danger' && (
            <div className="shrink-0 w-9 h-9 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2
              id="confirm-modal-title"
              className="text-base font-semibold text-theme-primary leading-snug"
            >
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-sm text-theme-secondary whitespace-pre-wrap leading-relaxed">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="shrink-0 p-1 rounded text-theme-tertiary hover:text-theme-primary hover:bg-surface-hover disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {children ? <div className="px-5 pb-3">{children}</div> : null}

        {/* Reason textarea */}
        {requireReason && (
          <div className="px-5 pb-3">
            <label className="block text-[10px] uppercase tracking-widest font-semibold text-theme-tertiary mb-1.5">
              {reasonLabel}
              {reasonRequired && <span className="text-rose-500 ml-0.5">*</span>}
            </label>
            <textarea
              ref={textareaRef}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              rows={3}
              placeholder={reasonPlaceholder}
              className="w-full rounded-lg border border-theme bg-surface-input px-3 py-2 text-sm text-theme-primary placeholder:text-theme-tertiary focus:outline-none focus:ring-2 focus:ring-brand-secondary/30 focus:border-brand-secondary resize-none"
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 px-5 pb-5 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg border border-theme text-sm text-theme-secondary hover:bg-surface-hover disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 ${confirmClasses}`}
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
